import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import Link from "@tiptap/extension-link";
import { useEffect, useState, useMemo } from "react";
import { MarkdownToolbar } from "./MarkdownToolbar";
import "./MarkdownEditor.css";

export type MarkdownFeature =
  | "bold"
  | "italic"
  | "heading"
  | "bulletList"
  | "orderedList"
  | "link"
  | "code"
  | "blockquote";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  features?: MarkdownFeature[];
  placeholder?: string;
}

export const MarkdownEditor = ({
  value,
  onChange,
  features = [
    "bold",
    "italic",
    "heading",
    "bulletList",
    "orderedList",
    "link",
    "code",
  ],
  placeholder = "Start typing...",
}: MarkdownEditorProps) => {
  const [viewMode, setViewMode] = useState<"rich" | "markdown">("rich");
  const [showToolbar, setShowToolbar] = useState(false);
  const [markdownContent, setMarkdownContent] = useState(value);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        bold: features.includes("bold") ? {} : false,
        italic: features.includes("italic") ? {} : false,
        heading: features.includes("heading") ? {} : false,
        bulletList: features.includes("bulletList") ? {} : false,
        orderedList: features.includes("orderedList") ? {} : false,
        code: features.includes("code") ? {} : false,
        blockquote: features.includes("blockquote") ? {} : false,
        codeBlock: features.includes("code") ? {} : false,
      }),
      Markdown,
      ...(features.includes("link")
        ? [
            Link.configure({
              openOnClick: false,
              HTMLAttributes: {
                rel: "noopener noreferrer",
                target: "_blank",
              },
            }),
          ]
        : []),
    ],
    [features]
  );

  const editor = useEditor({
    extensions,
    editorProps: {
      attributes: {
        class: "markdown-editor__content",
      },
    },
    onCreate: ({ editor }) => {
      if (value) {
        editor.commands.setContent(value, { contentType: "markdown" });
      }
    },
    onUpdate: ({ editor }) => {
      const markdown = editor.getMarkdown();
      setMarkdownContent(markdown);
      onChange(markdown);
    },
  });

  useEffect(() => {
    if (editor && value !== markdownContent) {
      editor.commands.setContent(value, { contentType: "markdown" });
      setMarkdownContent(value);
    }
  }, [editor, value, markdownContent]);

  const handleMarkdownChange = (newMarkdown: string) => {
    setMarkdownContent(newMarkdown);
    if (editor) {
      editor.commands.setContent(newMarkdown, { contentType: "markdown" });
    }
    onChange(newMarkdown);
  };

  const handleSwitchToMarkdown = () => {
    setViewMode("markdown");
    setShowToolbar(false);
  };

  const handleSwitchToRichText = () => {
    setViewMode("rich");
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="markdown-editor">
      {viewMode === "rich" && showToolbar && (
        <MarkdownToolbar
          editor={editor}
          features={features}
          onSwitchMode={handleSwitchToMarkdown}
        />
      )}

      {viewMode === "markdown" && (
        <div className="markdown-editor__mode-header">
          <div className="markdown-editor__mode-label">
            <span>Markdown Editor</span>
            <span className="markdown-editor__info-icon" title="Write raw markdown syntax">
              ⓘ
            </span>
          </div>
          <button
            type="button"
            className="markdown-editor__switch-link"
            onClick={handleSwitchToRichText}
          >
            Switch to Rich Text Editor
          </button>
        </div>
      )}

      {viewMode === "rich" ? (
        <EditorContent editor={editor} />
      ) : (
        <textarea
          className="markdown-editor__textarea"
          value={markdownContent}
          onChange={(e) => handleMarkdownChange(e.target.value)}
          placeholder={placeholder}
        />
      )}

      <div className="markdown-editor__footer">
        <button
          type="button"
          className={`markdown-editor__aa-toggle ${showToolbar ? "active" : ""}`}
          onClick={() => setShowToolbar(!showToolbar)}
          title={showToolbar ? "Hide formatting options" : "Show formatting options"}
        >
          Aa
        </button>
      </div>
    </div>
  );
};
